import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

export interface Prospect {
  id: string;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  notes?: string;
  status?: 'pending' | 'contacted' | 'successful' | 'failed';
  contactedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export type ProspectListRow = Database['public']['Tables']['prospect_lists']['Row'];
export type ProspectListInsert = Database['public']['Tables']['prospect_lists']['Insert'];

export interface ProspectList extends Omit<ProspectListRow, 'prospects'> {
  prospects: Prospect[];
}

export function useSupabaseProspectLists() {
  const [prospectLists, setProspectLists] = useState<ProspectList[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, session } = useAuth();
  const { toast } = useToast();

  const fetchProspectLists = async () => {
    const userId = user?.id || session?.user?.id;
    if (!userId) {
      console.log('No user ID available for prospect lists fetch');
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('prospect_lists')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .throwOnError();

      const transformedData = (data || []).map(row => ({
        ...row,
        prospects: Array.isArray(row.prospects) ? row.prospects as unknown as Prospect[] : []
      }));
      setProspectLists(transformedData);
    } catch (error) {
      console.error('Error fetching prospect lists:', error);
      toast({
        title: "Error",
        description: "Failed to fetch prospect lists",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addProspectList = async (listData: Omit<ProspectListInsert, 'id' | 'user_id' | 'created_at' | 'updated_at'> & { prospects?: Prospect[] }) => {
    try {
      const userId = user?.id || session?.user?.id;
      if (!userId) throw new Error('User not authenticated');

      // Prepare the insert payload with proper field mapping
      const insertPayload = {
        name: listData.name,
        description: listData.description,
        prospects: listData.prospects || [],
        tcpa_compliance: listData.tcpa_compliance || false,
        tcpa_acknowledged_at: listData.tcpa_acknowledged_at || null,
        tcpa_acknowledged_by: listData.tcpa_acknowledged_by || null,
        user_id: userId
      };

      const { data, error } = await supabase
        .from('prospect_lists')
        .insert([insertPayload])
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      const transformedData = {
        ...data,
        prospects: Array.isArray(data.prospects) ? data.prospects as unknown as Prospect[] : []
      };
      setProspectLists(prev => [transformedData, ...prev]);
      return transformedData;
    } catch (error) {
      console.error('Failed to create prospect list:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create prospect list",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateProspectList = async (id: string, updates: Partial<ProspectList>) => {
    try {
      const updateData = {
        ...updates,
        prospects: updates.prospects ? updates.prospects as any : undefined
      };
      
      const { data, error } = await supabase
        .from('prospect_lists')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const transformedData = {
        ...data,
        prospects: Array.isArray(data.prospects) ? data.prospects as unknown as Prospect[] : []
      };
      setProspectLists(prev => prev.map(list => 
        list.id === id ? { ...list, ...transformedData } : list
      ));
      return transformedData;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update prospect list",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteProspectList = async (id: string) => {
    try {
      const { error } = await supabase
        .from('prospect_lists')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setProspectLists(prev => prev.filter(list => list.id !== id));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete prospect list",
        variant: "destructive",
      });
      throw error;
    }
  };

  const acknowledgeTCPA = async (listId: string, acknowledgedBy: string = 'User') => {
    return updateProspectList(listId, {
      tcpa_compliance: true,
      tcpa_acknowledged_at: new Date().toISOString(),
      tcpa_acknowledged_by: acknowledgedBy
    });
  };

  const getCompanies = () => {
    const allProspects = prospectLists.flatMap(list => list.prospects);
    return Array.from(new Set(allProspects.map(p => p.company).filter(Boolean)));
  };

  // Initialize data when user becomes available
  useEffect(() => {
    const userId = user?.id || session?.user?.id;
    if (userId) {
      fetchProspectLists();
    }
  }, [user?.id, session?.user?.id]);

  return {
    prospectLists,
    loading,
    addProspectList,
    updateProspectList,
    deleteProspectList,
    acknowledgeTCPA,
    refreshProspectLists: fetchProspectLists,
    getCompanies,
  };
}